/**
 * Route Prefetching Utility
 * Preloads JavaScript chunks for routes when user hovers over links
 * Improves perceived navigation speed
 */

// Map routes to their lazy import functions
const routeModules: Record<string, () => Promise<unknown>> = {
  '/speisekarte-hochladen': () => import('@/pages/Upload'),
  '/preise': () => import('@/pages/Preise'),
  '/demo': () => import('@/pages/Demo'),
  '/faq': () => import('@/pages/FAQ'),
  '/profil': () => import('@/pages/Profil'),
  '/auth': () => import('@/pages/Auth'),
  '/vorteile': () => import('@/pages/Vorteile'),
  '/kennzeichnung': () => import('@/pages/KennzeichnungAlles'),
  '/kennzeichnung-alles': () => import('@/pages/KennzeichnungAlles'),
  '/wissen': () => import('@/pages/KennzeichnungAlles'),
  '/about': () => import('@/pages/About'),
  '/kontakt': () => import('@/pages/Kontakt'),
  '/impressum': () => import('@/pages/Impressum'),
  '/datenschutz': () => import('@/pages/Datenschutz'),
  '/agb': () => import('@/pages/AGB'),
  '/widerruf': () => import('@/pages/Widerruf'),
  '/cookies': () => import('@/pages/Cookies'),
  '/admin': () => import('@/pages/Admin'),
  '/analyseergebnisse': () => import('@/pages/Analyseergebnisse'),
};

// Track which routes have already been prefetched
const prefetchedRoutes = new Set<string>();

/**
 * Prefetch a route's JavaScript chunk
 * Safe to call multiple times - will only load once
 */
export function prefetchRoute(path: string): void {
  // Normalize path (remove hash, query params)
  const normalizedPath = path.split('?')[0].split('#')[0];
  
  // Skip if already prefetched or not a known route
  if (prefetchedRoutes.has(normalizedPath)) return;
  
  const moduleLoader = routeModules[normalizedPath];
  if (!moduleLoader) return;
  
  // Mark as prefetched immediately to avoid duplicate calls
  prefetchedRoutes.add(normalizedPath);
  
  // Use requestIdleCallback for non-blocking prefetch
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      moduleLoader().catch(() => {
        // Remove from set if prefetch failed so it can retry
        prefetchedRoutes.delete(normalizedPath);
      });
    }, { timeout: 2000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      moduleLoader().catch(() => {
        prefetchedRoutes.delete(normalizedPath);
      });
    }, 100);
  }
}

/**
 * Create an onMouseEnter handler for Link prefetching
 */
export function createPrefetchHandler(path: string): () => void {
  return () => prefetchRoute(path);
}

/**
 * Prefetch multiple routes at once (e.g., on app load)
 */
export function prefetchCriticalRoutes(): void {
  // Lightweight public routes – immer vorladen
  ['/preise', '/auth'].forEach((r) => prefetchRoute(r));
}

/**
 * Routen für eingeloggte Nutzer vorladen. Wird nach erfolgreichem Auth-Check
 * aufgerufen, damit der Wechsel ins Profil/Admin/Analyseergebnisse keinen
 * sichtbaren Lade-Flash mehr erzeugt.
 */
export function prefetchAuthenticatedRoutes(): void {
  ['/profil', '/analyseergebnisse', '/admin'].forEach((r) => prefetchRoute(r));
}
